import React, { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    DEPTH,
    HEADER_SETTINGS_DEFAULTS,
    SOCKET_SUB_TYPE,
    TRADING_TYPE,
} from 'src/common/constants';
import { getResolution, isEmptyObj, isObj } from 'src/common/helpers';
import { DEFAULT_SPOT_SYMBOL } from 'src/config';
import { useAuth } from 'src/context/AuthContext';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { useKycAndFeaturesConfig } from 'src/hooks/useKycAndFeaturesConfig';
import { MasterLayout } from 'src/layout/MasterLayout';
import { APP_URLS } from 'src/setup/client/app-urls';
import * as socketActions from 'src/setup/redux/slices/socketSlice';
import { saveCurrentSymbol, updateTvWidgetRef } from 'src/setup/redux/slices/spotSlice';
import { SOCKET_ENDPOINTS } from 'src/setup/socket/socket-endpoints';
import { SocketSubscriptionPayload, Symbol } from 'src/types';

import SpotPage from './SpotPage';

const SpotWrapper: FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { tradeSpotPlatformEnabled } = useKycAndFeaturesConfig();

    const { isAuthenticated } = useAuth();

    const spotSymbols = useAppSelector((state) => state.global.configV2.symbol);
    const symbol: Symbol = useAppSelector((state) => state.spot.currentSymbol);
    const { tvWidgetRef } = useAppSelector((state) => state.spot);
    const { spotSettings: chartSettings } = useAppSelector((state) => state.global);
    const { userVerifyInfoDirty, userFeaturesConfig } = useAppSelector((state) => state.user);

    const [socketPayload, setSocketPayload] = useState([] as Array<SocketSubscriptionPayload>);

    const spotSettings = chartSettings || HEADER_SETTINGS_DEFAULTS;

    useEffect(() => {
        if (
            (!userVerifyInfoDirty && !isAuthenticated) ||
            (userVerifyInfoDirty && isAuthenticated)
        ) {
            const pathArr = location.pathname.split('/');
            let symbolId = pathArr[2];

            if (userVerifyInfoDirty && isAuthenticated) {
                if (!tradeSpotPlatformEnabled) {
                    window.location.href = '/';
                    return;
                }
            }

            if (isObj(spotSymbols) && !isEmptyObj(spotSymbols)) {
                // Validate symbolId and navigate accordingly
                if (!Object.keys(spotSymbols).includes(symbolId)) {
                    const storedSymbol = localStorage.getItem('currentSpotSymbol');
                    symbolId = Object.keys(spotSymbols).includes(symbolId?.toUpperCase())
                        ? symbolId.toUpperCase()
                        : storedSymbol
                        ? storedSymbol
                        : Object.keys(spotSymbols).includes(DEFAULT_SPOT_SYMBOL)
                        ? DEFAULT_SPOT_SYMBOL
                        : Object.keys(spotSymbols)[1];

                    if (
                        userFeaturesConfig?.symbols &&
                        !userFeaturesConfig.symbols.includes(symbolId)
                    ) {
                        window.location.href = '/';
                        return;
                    }

                    navigate(`${APP_URLS.SPOT}/${symbolId}`, {
                        state: location,
                        replace: true,
                    });
                    return;
                }

                if (userFeaturesConfig?.symbols && !userFeaturesConfig.symbols.includes(symbolId)) {
                    window.location.href = '/';
                    return;
                }

                dispatch(socketActions.resetTradeData());
                dispatch(saveCurrentSymbol(spotSymbols[symbolId]));
            } else {
                symbolId = symbolId ?? localStorage.getItem('currentSpotSymbol');

                if (userFeaturesConfig?.symbols && !userFeaturesConfig.symbols.includes(symbolId)) {
                    window.location.href = '/';
                }
            }
        }
    }, [
        location,
        spotSymbols,
        isAuthenticated,
        userVerifyInfoDirty,
        userFeaturesConfig?.symbols,
        tradeSpotPlatformEnabled,
    ]);

    useEffect(() => {
        if (!isEmptyObj(symbol)) {
            let aggTradeDigits = DEPTH[symbol.digitMerge.split(',')[0]];
            aggTradeDigits = aggTradeDigits <= 0 ? aggTradeDigits - 1 : aggTradeDigits;
            const interval = getResolution(spotSettings.chartInterval);
            const socketPayload: SocketSubscriptionPayload[] = [
                {
                    url: SOCKET_ENDPOINTS.QUOTE_V1,
                    socketType: SOCKET_SUB_TYPE.TRADE_DATA,
                    subscriptions: [
                        {
                            id: 'broker',
                            topic: 'slowBroker',
                            event: 'sub',
                            params: {
                                org: Number(symbol.orgId),
                                realtimeInterval: '24h',
                                binary: false,
                            },
                        },
                        {
                            id: 'realtimes',
                            topic: 'realtimes',
                            event: 'sub',
                            symbol: `${symbol.exchangeId}.${symbol.symbolName}`,
                            params: { binary: true, realtimeInterval: '24h' },
                        },
                        {
                            id: `kline_${symbol.exchangeId}${symbol.symbolId}${interval}`,
                            topic: `kline_${interval}`,
                            event: 'sub',
                            symbol: `${symbol.exchangeId}.${symbol.symbolId}`,
                            params: {
                                binary: true,
                                klineType: `${interval}`,
                                limit: 1500,
                                realtimeInterval: '24h',
                            },
                        },
                        {
                            id: `${symbol.exchangeId}.${symbol.symbolId}1`,
                            topic: 'mergedDepth',
                            event: 'sub',
                            symbol: `${symbol.exchangeId}.${symbol.symbolId}`,
                            limit: 22,
                            params: { dumpScale: aggTradeDigits, binary: true },
                        },
                        {
                            id: `trade${symbol.exchangeId}.${symbol.symbolId}`,
                            topic: 'trade',
                            event: 'sub',
                            limit: 60,
                            symbol: `${symbol.exchangeId}.${symbol.symbolId}`,
                            params: { org: Number(symbol.orgId), binary: true },
                        },
                    ],
                },
            ];
            if (isAuthenticated) {
                socketPayload.push({
                    url: SOCKET_ENDPOINTS.USER,
                    socketType: SOCKET_SUB_TYPE.USER,
                    subscriptions: [
                        {
                            id: 'order',
                            topic: 'order',
                            event: 'sub',
                            params: { org: Number(symbol.orgId), binary: true },
                        },
                        {
                            id: 'plan_order',
                            topic: 'plan_order',
                            event: 'sub',
                            params: { org: Number(symbol.orgId), binary: true },
                        },
                        {
                            id: 'match',
                            topic: 'match',
                            event: 'sub',
                            params: { org: Number(symbol.orgId), binary: true },
                        },
                        {
                            id: 'balance',
                            topic: 'balance',
                            event: 'sub',
                        },
                    ],
                });
            }
            setSocketPayload(socketPayload);
        }
    }, [symbol, spotSettings, isAuthenticated]);

    useEffect(() => {
        return () => {
            if (!location.pathname.includes(APP_URLS.SPOT)) {
                const currentWidget = tvWidgetRef?.current._ready;
                if (currentWidget) {
                    try {
                        currentWidget?.remove();
                    } catch (error) {
                        dispatch(updateTvWidgetRef(null));
                    }
                    dispatch(updateTvWidgetRef(null));
                }
            }
        };
    }, [tvWidgetRef]);

    return (
        <MasterLayout>
            <SpotPage payloads={socketPayload} tradeType={TRADING_TYPE.SPOT} />
        </MasterLayout>
    );
};

export default SpotWrapper;
